import { createFileRoute } from '@tanstack/react-router'
import { getHeroSlides, getCoreValues, getReviews, getHours, getSiteConfig, getBakeryStats, getMenuItems, getGalleryImages } from '#/lib/content'
import { useCmsContent, useCmsObjectContent } from '#/hooks/useCmsContent'
// import Container from '#/components/layout/Container'
// import Section from '#/components/layout/Section'
import HeroCarousel from '#/components/home/HeroCarousel'
import CoreValues from '#/components/home/CoreValues'
import BakeryStats from '#/components/home/BakeryStats'
import GalleryCarousel from '#/components/home/GalleryCarousel'
import ReviewsCarousel from '#/components/home/ReviewsCarousel'
import MapHours from '#/components/home/MapHours'
// import WeeklyCalendar from '#/components/menu/WeeklyCalendar'
import { seoMeta, canonicalLink, SITE_URL } from '#/lib/seo'
import type { SiteConfig, BusinessHours } from '#/lib/content-types'

function buildLocalBusinessJsonLd(config: SiteConfig, hours: BusinessHours[]) {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const openHours = hours
    .filter((h) => !h.closed)
    .map((h) => {
      const day = dayNames[h.dayOfWeek]?.slice(0, 2)
      return `${day} ${h.open}-${h.close}`
    })

  return {
    '@context': 'https://schema.org',
    '@type': 'Bakery',
    name: config.businessName,
    description:
      'A family-run artisan bakery in Ferndale, crafting fresh bread, pastries, and community one loaf at a time.',
    url: SITE_URL,
    telephone: config.phone,
    email: config.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: config.address.street,
      addressLocality: config.address.city,
      addressRegion: config.address.state,
      postalCode: config.address.zip,
      addressCountry: 'US',
    },
    openingHours: openHours,
    sameAs: config.socialLinks.map((l) => l.url),
  }
}

export const Route = createFileRoute('/')({
  head: ({ match }) => {
    const loaderData = match.loaderData
    const scripts: Array<Record<string, string>> = []
    if (loaderData?.config && loaderData?.hours) {
      scripts.push({
        type: 'application/ld+json',
        children: JSON.stringify(
          buildLocalBusinessJsonLd(loaderData.config, loaderData.hours),
        ),
      })
    }
    return {
      meta: seoMeta({ path: '/' }),
      links: [canonicalLink('/')],
      scripts,
    }
  },
  loader: async () => {
    const [slides, values, reviews, hours, config, stats, menuItems, gallery] = await Promise.all([
      getHeroSlides(),
      getCoreValues(),
      getReviews(),
      getHours(),
      getSiteConfig(),
      getBakeryStats(),
      getMenuItems(),
      getGalleryImages(),
    ])
    return { slides, values, reviews, hours, config, stats, menuItems, gallery }
  },
  component: HomePage,
})

function HomePage() {
  const data = Route.useLoaderData()
  const slides = useCmsContent('hero-slides', data.slides)
  const values = useCmsContent('core-values', data.values)
  const reviews = useCmsContent('reviews', data.reviews)
  const hours = useCmsContent('hours', data.hours)
  const config = useCmsObjectContent('site-config', data.config)
  const stats = useCmsContent('bakery-stats', data.stats)
  // const menuItems = useCmsContent('menu-items', data.menuItems)
  const gallery = useCmsContent('gallery-images', data.gallery)

  return (
    <main>
      <HeroCarousel slides={slides} orderUrl={config.orderOnlineUrl} />
      {/* Weekly schedule hidden at client's request. Re-enable by uncommenting
          this block, the `menuItems` hook above, and the Container/Section/WeeklyCalendar imports.
      <Section tone="low" className="py-8 sm:py-12">
        <Container>
          <h2 className="mb-6 text-center font-serif text-2xl font-bold text-on-surface sm:text-3xl">
            This Week&apos;s Schedule
          </h2>
          <WeeklyCalendar items={menuItems} hours={hours} />
        </Container>
      </Section>
      */}
      <CoreValues values={values} />
      <BakeryStats stats={stats} />
      <GalleryCarousel images={gallery} />
      <ReviewsCarousel reviews={reviews} />
      <MapHours hours={hours} config={config} />
    </main>
  )
}
